import { permissionGroupDotColor, permissionGroupTextColor } from './permissionGroupColor';

// Provenance in one line, e.g. "roles/odo.billingManager › billing.manager ›
// billing.get". Kind renders distinctly (role: gray-800, bundle: violet italic).

type ChainNodeKind = 'role' | 'bundle';

// A role key always carries the `roles/` prefix, a bundle key never does.
function nodeKind(step: string): ChainNodeKind {
    return step.startsWith('roles/') ? 'role' : 'bundle';
}

interface ChainProps {
    chain: readonly string[];
    /** Optional terminal node after the last container; requires `group` too, ignored alone. */
    permission?: string;
    /** The permission's family, for the terminal node's dot/text colour - see `permission`. */
    group?: string;
    className?: string;
}

export function Chain({ chain, permission, group, className = '' }: ChainProps) {
    if (chain.length === 0) return null;

    return (
        <span
            title={permission && group ? [...chain, permission].join(' › ') : chain.join(' › ')}
            className={`inline-flex items-center gap-1 font-mono text-[11px] min-w-0 ${className}`.trim()}
        >
            {chain.map((step, index) => {
                const isLastContainer = index === chain.length - 1;
                const kind = nodeKind(step);
                return (
                    <span
                        key={`${step}-${index}`}
                        className="inline-flex items-center gap-1 min-w-0"
                    >
                        <span
                            className={`truncate font-semibold ${
                                kind === 'bundle' ? 'italic text-violet-700' : 'text-gray-800'
                            }`}
                        >
                            {step}
                        </span>
                        {(!isLastContainer || (permission && group)) && (
                            <span className="text-gray-300 shrink-0">›</span>
                        )}
                    </span>
                );
            })}
            {permission && group && (
                <span className="inline-flex items-center gap-1 min-w-0">
                    <span
                        className={`h-1.5 w-1.5 rounded-full shrink-0 ${permissionGroupDotColor(group)}`}
                    />
                    <span className={`truncate ${permissionGroupTextColor(group)}`}>
                        {permission}
                    </span>
                </span>
            )}
        </span>
    );
}
